import { UFOExperience } from '@atlaskit/ufo/experience';
import { ExperiencePerformanceTypes, ExperienceTypes } from '@atlaskit/ufo/experience-types';
import type { CustomData } from '@atlaskit/ufo/types';

export const ExperienceName: {
	readonly LOAD_PANEL: 'aiMateLoadPanel';
	readonly LOAD_PANEL_COMPOSABLE_EDITOR: 'aiMateLoadPanelComposableEditor';
	readonly LOAD_INLINE_EDITOR: 'aiMateLoadInlineComposableEditor';
	readonly LOAD_BROWSE_AGENTS: 'aiMateLoadBrowseAgents';
	readonly LOAD_CREATE_AGENT: 'aiMateLoadCreateAgent';
	readonly LOAD_AUTO_SUGGEST_SKILLS_LEGACY: 'aiMateAutoSuggestSkillsLegacy';
	readonly LOAD_AUTO_SUGGEST_SKILLS_UNIFIED: 'aiMateAutoSuggestSkillsUnified';
	readonly LOAD_CHAT_PROMPT_INPUT_SKILLS: 'aiMateChatPromptInputSkillsLoad';
	readonly LOAD_INLINE_CHAT_PROMPT_INPUT_SKILLS: 'aiMateInlineChatPromptInputSkillsLoad';
	readonly FETCH_CHAT: 'aiMateFetchChat';
	readonly PREFETCH_CHAT: 'aiMatePrefetchChat';
	readonly FETCH_AGENTS: 'aiMateFetchAgents';
	readonly FETCH_AGENTS_PAGINATED: 'aiMateFetchAgentsPaginated';
	readonly FETCH_AGENTS_OVERRIDES: 'aiMateFetchAgentsOverrides';
	readonly FETCH_AGENT_BY_AGENT_ID: 'aiMateFetchAgentByAgentId';
	readonly FETCH_SCENARIO_LIST: 'aiMateFetchScenarioList';
	readonly FETCH_AGENT_BY_IDENTITY_ACCOUNT_ID: 'aiMateFetchAgentByIdentityAccountId';
	readonly FETCH_AGENT_BY_EXTERNAL_CONFIG_REFERENCE: 'aiMateFetchAgentByExternalConfigReference';
	readonly FETCH_AGENT_DEBUG_MODAL_DATA: 'aiMateFetchAgentDebugModalData';
	readonly FETCH_AGENT_DEBUG_MODAL_CONVERSATION_REVIEW: 'aiMateFetchAgentDebugModalDataConversationReview';
	readonly CREATE_AGENT: 'aiMateCreateAgent';
	readonly UPDATE_AGENT: 'aiMateUpdateAgent';
	readonly DELETE_AGENT: 'aiMateDeleteAgent';
	readonly DUPLICATE_AGENT: 'aiMateDuplicateAgent';
	readonly HYDRATE_AGENT_DATA: 'aiMateHydrateAgentData';
	readonly HYDRATE_SKILL_METADATA: 'aiMateHydrateSkillMetadata';
	readonly FAVOURITE_AGENT: 'aiMateFavouriteAgent';
	readonly UNFAVOURITE_AGENT: 'aiMateUnfavouriteAgent';
	readonly CLEAR_CHAT: 'aiMateClearChat';
	readonly SEND_MESSAGE: 'aiMateSendMessage';
	readonly SEND_MESSAGE_ROVO_CUSTOM_AGENT: 'aiMateSendMessageRovoCustomAgent';
	readonly SEND_DR_MESSAGE: 'aiMateSendDRMessage';
	readonly SEND_TD_MESSAGE: 'aiMateSendThinkDeeperMessage';
	readonly SEND_TASK_MODE_MESSAGE: 'aiMateSendTaskModeMessage';
	readonly SEND_CONFIRMED_TOOLS_MESSAGE: 'aiMateSendConfirmedToolsMessage';
	readonly SEND_CONFIRMED_TOOLS_TASK_MODE_MESSAGE: 'aiMateSendConfirmedToolsTaskModeMessage';
	readonly RECONNECT_TO_ACTIVE_STREAM: 'aiMateReconnectToActiveStream';
	readonly ABORT_AGENT_STREAM: 'aiMateAbortAgentStream';
	readonly ANSWER_NOW: 'aiMateAnswerNow';
	readonly HYDRATE_CONVERSATION: 'aiMateHydrateConversation';
	readonly GET_CONVERSATION_CHANNELS: 'aiMateGetConversationChannels';
	readonly GET_CONVERSATION_CHANNELS_INITIAL_LOAD: 'aiMateGetConversationChannelsInitialLoad';
	readonly LOAD_MORE_CONVERSATION_CHANNELS: 'aiMateLoadMoreConversationChannels';
	readonly UPDATE_CONVERSATION_CHANNEL: 'aiMateUpdateConversationChannel';
	readonly CREATE_SEEDED_CONVERSATION_CHANNEL: 'aiMateCreateSeededConversationChannel';
	readonly PUBSUB_LISTENER: 'aiMatePubSubListener';
	readonly FETCH_AGENT_TENANT_DATA: 'aiMateFetchAgentTenantData';
	readonly RESOLVE_CONVERSATION_ACTION: 'aiMateResolveConversationAction';
	readonly BULK_RESOLVE_CONVERSATION_ACTIONS: 'aiMateBulkResolveConversationActions';
	readonly MESSAGE_RENDERER: 'aiMateMessageRenderer';
	readonly FETCH_CONTEXTUAL_STARTERS: 'aiMateFetchContextualStarters';
	readonly FETCH_DYNAMIC_CONVERSATION_STARTERS: 'aiMateFetchDynamicConversationStarters';
	readonly GENERATE_CONVERSATION_STARTERS: 'aiMateGenerateConversationStarters';
	readonly FETCH_AGENT_TEAM_DATA: 'aiMateFetchAgentTeamData';
	readonly FETCH_CONNECTED_KNOWLEDGE_SOURCES: 'aiMateFetchConnectedKnowledgeSources';
	readonly FETCH_CONNECTED_KNOWLEDGE_SOURCES_STORE: 'aiMateFetchKnowledgeSourcesStore';
	readonly PREFETCH_CONNECTED_KNOWLEDGE_SOURCES_STORE: 'aiMatePrefetchKnowledgeSourcesStore';
	readonly FETCH_AGENT_ACTIONS: 'aiMateFetchAgentActions';
	readonly CHANGE_AGENT_OWNER: 'aiMateChangeAgentOwner';
	readonly GET_AGENT_PERMISSIONS: 'aiMateGetAgentPermissions';
	readonly AIFC_FINALIZE_CONTENT: 'aiMateAIFCFinalizeContent';
	readonly PREVIEW_CONTENT: 'aiMatePreviewContent';
	readonly PREVIEW_CARD_RENDER: 'aiMatePreviewCardRender';
	readonly JIRA_PREVIEW_CARD_RENDER: 'aiMateJiraPreviewCardRender';
	readonly JSM_PREVIEW_CARD_RENDER: 'aiMateJsmPreviewCardRender';
	readonly FETCH_AGENT_SITE_PERMISSIONS: 'aiMateFetchAgentSitePermissions';
	readonly PREFETCH_AGENT_SITE_PERMISSIONS: 'aiMatePrefetchAgentSitePermissions';
	readonly SEND_MESSAGE_NL_AGENT: 'aiMateSendMessageNLAgent';
	readonly FILTERED_CLIENT_ERROR: 'aiMateFilteredClientError';
	readonly GET_RECOMMENDED_AGENTS: 'aiMateGetRecommendedAgents';
	readonly PREFETCH_RECOMMENDED_AGENTS: 'aiMatePrefetchRecommendedAgents';
	readonly PREFETCH_RECOMMENDED_SPACES: 'aiMatePrefetchRecommendedSpaces';
	readonly OPEN_RECOMMENDED_SPACES: 'aiMateOpenRecommendedSpaces';
	readonly UPLOAD_FILES_TO_CONVERSATION: 'aiMateUploadFilesToConversation';
	readonly VALIDATE_FILES: 'aiMateValidateFiles';
	readonly GET_CONVERSATION_FILES: 'aiMateGetConversationFiles';
	readonly AIFC_MESSAGE: 'aiMateAifcMessage';
	readonly AIFC_LOAD_STAGING_AREA_MODAL: 'aiMateLoadStagingAreaModal';
	readonly AIFC_TWC_ONBOARDING_MODAL_LAUNCH: 'aiMateTwcAifcOnboardingModalLaunch';
	readonly AIFC_LOAD_CHAT_ONLY_CREATION_MODAL: 'aiMateLoadChatOnlyCreationModal';
	readonly AIFC_LOAD_DRAFTS_SECTION: 'aiMateLoadDraftsSection';
	readonly AIFC_GENERATE_CONTENT: 'aiMateAifcGenerateContent';
	readonly AIFC_GENERATE_CONTENT_IN_CHAT: 'aiMateAifcGenerateContentInChat';
	readonly AIFC_INITIAL_DATA_RECEIVED: 'aiMateAifcInitialDataReceived';
	readonly AIFC_ALL_DATA_HANDLED: 'aiMateAifcAllDataHandled';
	readonly AIFC_UPDATE_CONTENT: 'aiMateAifcUpdateContent';
	readonly JIRA_SAVE_DRAFT_WORK_ITEM: 'aiMateJiraSaveDraftWorkItem';
	readonly JIRA_RESOLVE_DRAFT_WORK_ITEM: 'aiMateJiraResolveDraftWorkItem';
	readonly JIRA_CREATE_WORK_ITEMS_BULK: 'aiMateJiraCreateWorkItemsBulk';
	/**
	 * Customer-visible reliability experiences for the Jira Rovo "create work
	 * item" scenario, split into its two moments and emitted as two separate
	 * experiences:
	 *   - PLAN: Rovo presented a valid create proposal to the user.
	 *   - EXECUTION: the confirmed create actually wrote to Jira.
	 *
	 * Both feed the FY27 99.5% success-minutes reliability KR (ATLAS-129636).
	 * The value is emitted verbatim as the `task` attribute and read by the ESLA
	 * pipeline as its `experience`, so the seed rows in `observability-cloud-sla`
	 * (critical_experiences_v2.csv, product `rovo` — AIMate events are normalized
	 * to product=rovo in csar_aggregated_raw.sql) must match these exact strings,
	 * and both must be registered in DataPortal as allowed `task` values or the
	 * events are dropped at ingestion.
	 */
	readonly ROVO_SKILLS_PLAN_WORK_ITEM_CREATE: 'RovoSkillsPlanWorkItemCreate';
	readonly ROVO_SKILLS_EXECUTION_WORK_ITEM_CREATE: 'RovoSkillsExecutionWorkItemCreate';
	readonly ROVO_SKILLS_PLAN_WORK_ITEM_UPDATE: 'RovoSkillsPlanWorkItemUpdate';
	readonly ROVO_SKILLS_EXECUTION_WORK_ITEM_UPDATE: 'RovoSkillsExecutionWorkItemUpdate';
	readonly UPDATE_CONTENT_IN_CHAT: 'aiMateUpdateContentInChat';
	readonly AIFC_MODAL_SESSION: 'aiMateAifcModalSession';
	readonly CSM_WIDGET_RENDER: 'csmWidgetRender';
	readonly CSM_AGENT_IDENTITY_NAME: 'csmAgentIdentityName';
	readonly CSM_AGENT_IDENTITY_COMPANY: 'csmAgentIdentityCompany';
	readonly CSM_AGENT_IDENTITY_TONE: 'csmAgentIdentityTone';
	readonly CSM_AGENT_IDENTITY_GREETING_MESSAGE: 'csmAgentIdentityGreetingMessage';
	readonly CSM_AGENT_IDENTITY_CONVERSATION_STARTERS: 'csmAgentIdentityConversationStarters';
	readonly CSM_AGENT_IDENTITY_GENERATE_CONVERSATION_STARTERS: 'csmAgentIdentityGenerateConversationStarters';
	readonly GET_AUTO_APPLY_STATE: 'aiMateGetAutoApplyState';
	readonly SET_AUTO_APPLY_STATE: 'aiMateSetAutoApplyState';
	readonly AGENTS_MODAL_AND_SIDEBAR_LIST: 'aiMateAgentsModalAndSidebarList';
	readonly AGENTS_MODAL_FAVOURITE_AGENTS: 'aiMateAgentsModalFavouriteAgents';
	readonly AGENTS_FULLSCREEN_AGENT_LIST: 'aiMateAgentsFullscreenAgentList';
	readonly STUDIO_TRIGGER_SOLUTION_PLAN: 'aiMateStudioTriggerSolutionPlan';
	readonly MY_PROJECTS_LIST_QUERY: 'aiMateMyProjectsListQuery';
	readonly STUDIO_UPDATE_AGENT: 'aiMateStudioUpdateAgent';
	readonly STUDIO_AUTOMATION_CREATE_RULE: 'aiMateStudioAutomationCreateRule';
	readonly THREADS_LIST_QUERY: 'aiMateThreadsListQuery';
	readonly WARM_CONVERSATION_CACHE: 'aiMateWarmConversationCache';
	readonly WARM_CONTEXT_BANNER_LINK: 'aiMateWarmContextBannerLink';
	readonly AIFC_LOAD_PROMPT_SUGGESTIONS: 'aiMateAifcLoadPromptSuggestions';
	readonly REMIX_START: 'aiMateRemixStart';
	readonly REMIX_AIFC_HANDOFF: 'aiMateRemixAifcHandoff';
	readonly FETCH_PROMPTS: 'aiMateFetchPrompts';
	readonly FETCH_INSIGHTS_STATUS: 'aiMateFetchInsightsStatus';
	readonly FETCH_INSIGHTS_FEED: 'aiMateFetchInsightsFeed';
	readonly REMIX_RENDERS_IN_MODAL: 'aiMateRemixRendersInModal';
	readonly REMIX_CREATE: 'aiMateRemixCreate';
	readonly REMIX_OPEN_MENU: 'aiMateRemixOpenMenu';
	readonly REMIX_SELECT_OPTION: 'aiMateRemixSelectOption';
	readonly REMIX_SHOW_LOADING: 'aiMateRemixShowLoading';
	readonly REMIX_REGENERATE: 'aiMateRemixRegenerate';
	readonly REMIX_INSERT: 'aiMateRemixInsert';
	readonly REMIX_PASTE: 'aiMateRemixPaste';
	readonly REMIX_DIAGRAM_PASTE_FINALIZE: 'aiMateRemixDiagramPasteFinalize';
	readonly FETCH_TASKS: 'aiMateFetchTasks';
	readonly LOAD_STICKY_SKILL_LISTENER: 'aiMateLoadStickySkillListener';
	readonly REMIX_DUPLICATE_APP: 'aiMateRemixDuplicateApp';
	readonly STUDIO_CREATE_AGENT: 'aiMateStudioCreateAgent';
	readonly STUDIO_CREATE_AGENT_ASSIGN: 'aiMateStudioCreateAgentAssign';
	readonly STUDIO_CREATE_AGENT_INVOKE: 'aiMateStudioCreateAgentInvoke';
	readonly STUDIO_CREATE_AGENT_BUILD_AND_CHAT: 'aiMateStudioCreateAgentBuildAndChat';
	readonly STUDIO_CREATE_AGENT_REFINE_IN_STUDIO: 'aiMateStudioCreateAgentRefineInStudio';
	readonly STUDIO_CREATE_AGENT_RENDER: 'aiMateStudioCreateAgentRender';
	readonly SHARE_CONVERSATION_SESSION: 'aiMateShareConversationSession';
	readonly MANAGE_CONVERSATION_SHARES: 'aiMateManageConversationShares';
	readonly VIEW_CONVERSATION_SHARE: 'aiMateViewConversationShare';
} = {
	LOAD_PANEL: 'aiMateLoadPanel',
	LOAD_PANEL_COMPOSABLE_EDITOR: 'aiMateLoadPanelComposableEditor',
	LOAD_INLINE_EDITOR: 'aiMateLoadInlineComposableEditor',
	LOAD_BROWSE_AGENTS: 'aiMateLoadBrowseAgents',
	LOAD_CREATE_AGENT: 'aiMateLoadCreateAgent',
	LOAD_AUTO_SUGGEST_SKILLS_LEGACY: 'aiMateAutoSuggestSkillsLegacy',
	LOAD_AUTO_SUGGEST_SKILLS_UNIFIED: 'aiMateAutoSuggestSkillsUnified',
	LOAD_CHAT_PROMPT_INPUT_SKILLS: 'aiMateChatPromptInputSkillsLoad',
	LOAD_INLINE_CHAT_PROMPT_INPUT_SKILLS: 'aiMateInlineChatPromptInputSkillsLoad',
	FETCH_CHAT: 'aiMateFetchChat',
	PREFETCH_CHAT: 'aiMatePrefetchChat',
	FETCH_AGENTS: 'aiMateFetchAgents',
	FETCH_AGENTS_PAGINATED: 'aiMateFetchAgentsPaginated',
	FETCH_AGENTS_OVERRIDES: 'aiMateFetchAgentsOverrides',
	FETCH_AGENT_BY_AGENT_ID: 'aiMateFetchAgentByAgentId',
	FETCH_SCENARIO_LIST: 'aiMateFetchScenarioList',
	FETCH_AGENT_BY_IDENTITY_ACCOUNT_ID: 'aiMateFetchAgentByIdentityAccountId',
	FETCH_AGENT_BY_EXTERNAL_CONFIG_REFERENCE: 'aiMateFetchAgentByExternalConfigReference',
	FETCH_AGENT_DEBUG_MODAL_DATA: 'aiMateFetchAgentDebugModalData',
	FETCH_AGENT_DEBUG_MODAL_CONVERSATION_REVIEW: 'aiMateFetchAgentDebugModalDataConversationReview',
	CREATE_AGENT: 'aiMateCreateAgent',
	UPDATE_AGENT: 'aiMateUpdateAgent',
	DELETE_AGENT: 'aiMateDeleteAgent',
	DUPLICATE_AGENT: 'aiMateDuplicateAgent',
	HYDRATE_AGENT_DATA: 'aiMateHydrateAgentData',
	HYDRATE_SKILL_METADATA: 'aiMateHydrateSkillMetadata',
	FAVOURITE_AGENT: 'aiMateFavouriteAgent',
	UNFAVOURITE_AGENT: 'aiMateUnfavouriteAgent',
	CLEAR_CHAT: 'aiMateClearChat',
	SEND_MESSAGE: 'aiMateSendMessage',
	SEND_MESSAGE_ROVO_CUSTOM_AGENT: 'aiMateSendMessageRovoCustomAgent',
	SEND_DR_MESSAGE: 'aiMateSendDRMessage',
	SEND_TD_MESSAGE: 'aiMateSendThinkDeeperMessage',
	SEND_TASK_MODE_MESSAGE: 'aiMateSendTaskModeMessage',
	SEND_CONFIRMED_TOOLS_MESSAGE: 'aiMateSendConfirmedToolsMessage',
	SEND_CONFIRMED_TOOLS_TASK_MODE_MESSAGE: 'aiMateSendConfirmedToolsTaskModeMessage',
	RECONNECT_TO_ACTIVE_STREAM: 'aiMateReconnectToActiveStream',
	ABORT_AGENT_STREAM: 'aiMateAbortAgentStream',
	ANSWER_NOW: 'aiMateAnswerNow',
	HYDRATE_CONVERSATION: 'aiMateHydrateConversation',
	GET_CONVERSATION_CHANNELS: 'aiMateGetConversationChannels',
	GET_CONVERSATION_CHANNELS_INITIAL_LOAD: 'aiMateGetConversationChannelsInitialLoad',
	LOAD_MORE_CONVERSATION_CHANNELS: 'aiMateLoadMoreConversationChannels',
	UPDATE_CONVERSATION_CHANNEL: 'aiMateUpdateConversationChannel',
	CREATE_SEEDED_CONVERSATION_CHANNEL: 'aiMateCreateSeededConversationChannel',
	PUBSUB_LISTENER: 'aiMatePubSubListener',
	FETCH_AGENT_TENANT_DATA: 'aiMateFetchAgentTenantData',
	RESOLVE_CONVERSATION_ACTION: 'aiMateResolveConversationAction',
	BULK_RESOLVE_CONVERSATION_ACTIONS: 'aiMateBulkResolveConversationActions',
	MESSAGE_RENDERER: 'aiMateMessageRenderer',
	FETCH_CONTEXTUAL_STARTERS: 'aiMateFetchContextualStarters',
	FETCH_DYNAMIC_CONVERSATION_STARTERS: 'aiMateFetchDynamicConversationStarters',
	GENERATE_CONVERSATION_STARTERS: 'aiMateGenerateConversationStarters',
	FETCH_AGENT_TEAM_DATA: 'aiMateFetchAgentTeamData',
	FETCH_CONNECTED_KNOWLEDGE_SOURCES: 'aiMateFetchConnectedKnowledgeSources',
	FETCH_CONNECTED_KNOWLEDGE_SOURCES_STORE: 'aiMateFetchKnowledgeSourcesStore',
	PREFETCH_CONNECTED_KNOWLEDGE_SOURCES_STORE: 'aiMatePrefetchKnowledgeSourcesStore',
	FETCH_AGENT_ACTIONS: 'aiMateFetchAgentActions',
	CHANGE_AGENT_OWNER: 'aiMateChangeAgentOwner',
	GET_AGENT_PERMISSIONS: 'aiMateGetAgentPermissions',
	AIFC_FINALIZE_CONTENT: 'aiMateAIFCFinalizeContent',
	PREVIEW_CONTENT: 'aiMatePreviewContent',
	PREVIEW_CARD_RENDER: 'aiMatePreviewCardRender',
	JIRA_PREVIEW_CARD_RENDER: 'aiMateJiraPreviewCardRender',
	JSM_PREVIEW_CARD_RENDER: 'aiMateJsmPreviewCardRender',
	FETCH_AGENT_SITE_PERMISSIONS: 'aiMateFetchAgentSitePermissions',
	PREFETCH_AGENT_SITE_PERMISSIONS: 'aiMatePrefetchAgentSitePermissions',
	SEND_MESSAGE_NL_AGENT: 'aiMateSendMessageNLAgent',
	FILTERED_CLIENT_ERROR: 'aiMateFilteredClientError',
	GET_RECOMMENDED_AGENTS: 'aiMateGetRecommendedAgents',
	PREFETCH_RECOMMENDED_AGENTS: 'aiMatePrefetchRecommendedAgents',
	PREFETCH_RECOMMENDED_SPACES: 'aiMatePrefetchRecommendedSpaces',
	OPEN_RECOMMENDED_SPACES: 'aiMateOpenRecommendedSpaces',
	UPLOAD_FILES_TO_CONVERSATION: 'aiMateUploadFilesToConversation',
	VALIDATE_FILES: 'aiMateValidateFiles',
	GET_CONVERSATION_FILES: 'aiMateGetConversationFiles',
	AIFC_MESSAGE: 'aiMateAifcMessage',
	AIFC_LOAD_STAGING_AREA_MODAL: 'aiMateLoadStagingAreaModal',
	AIFC_TWC_ONBOARDING_MODAL_LAUNCH: 'aiMateTwcAifcOnboardingModalLaunch',
	AIFC_LOAD_CHAT_ONLY_CREATION_MODAL: 'aiMateLoadChatOnlyCreationModal',
	AIFC_LOAD_DRAFTS_SECTION: 'aiMateLoadDraftsSection',
	AIFC_GENERATE_CONTENT: 'aiMateAifcGenerateContent',
	AIFC_GENERATE_CONTENT_IN_CHAT: 'aiMateAifcGenerateContentInChat',
	AIFC_INITIAL_DATA_RECEIVED: 'aiMateAifcInitialDataReceived',
	AIFC_ALL_DATA_HANDLED: 'aiMateAifcAllDataHandled',
	AIFC_UPDATE_CONTENT: 'aiMateAifcUpdateContent',
	UPDATE_CONTENT_IN_CHAT: 'aiMateUpdateContentInChat',
	AIFC_MODAL_SESSION: 'aiMateAifcModalSession',
	CSM_WIDGET_RENDER: 'csmWidgetRender',
	CSM_AGENT_IDENTITY_NAME: 'csmAgentIdentityName',
	CSM_AGENT_IDENTITY_COMPANY: 'csmAgentIdentityCompany',
	CSM_AGENT_IDENTITY_TONE: 'csmAgentIdentityTone',
	CSM_AGENT_IDENTITY_GREETING_MESSAGE: 'csmAgentIdentityGreetingMessage',
	CSM_AGENT_IDENTITY_CONVERSATION_STARTERS: 'csmAgentIdentityConversationStarters',
	CSM_AGENT_IDENTITY_GENERATE_CONVERSATION_STARTERS: 'csmAgentIdentityGenerateConversationStarters',
	GET_AUTO_APPLY_STATE: 'aiMateGetAutoApplyState',
	SET_AUTO_APPLY_STATE: 'aiMateSetAutoApplyState',
	AGENTS_MODAL_AND_SIDEBAR_LIST: 'aiMateAgentsModalAndSidebarList',
	AGENTS_MODAL_FAVOURITE_AGENTS: 'aiMateAgentsModalFavouriteAgents',
	AGENTS_FULLSCREEN_AGENT_LIST: 'aiMateAgentsFullscreenAgentList',
	STUDIO_TRIGGER_SOLUTION_PLAN: 'aiMateStudioTriggerSolutionPlan',
	MY_PROJECTS_LIST_QUERY: 'aiMateMyProjectsListQuery',
	STUDIO_UPDATE_AGENT: 'aiMateStudioUpdateAgent',
	STUDIO_AUTOMATION_CREATE_RULE: 'aiMateStudioAutomationCreateRule',
	THREADS_LIST_QUERY: 'aiMateThreadsListQuery',
	WARM_CONVERSATION_CACHE: 'aiMateWarmConversationCache',
	WARM_CONTEXT_BANNER_LINK: 'aiMateWarmContextBannerLink',
	AIFC_LOAD_PROMPT_SUGGESTIONS: 'aiMateAifcLoadPromptSuggestions',
	REMIX_START: 'aiMateRemixStart',
	REMIX_AIFC_HANDOFF: 'aiMateRemixAifcHandoff',
	FETCH_PROMPTS: 'aiMateFetchPrompts',
	FETCH_INSIGHTS_STATUS: 'aiMateFetchInsightsStatus',
	FETCH_INSIGHTS_FEED: 'aiMateFetchInsightsFeed',
	REMIX_RENDERS_IN_MODAL: 'aiMateRemixRendersInModal',
	REMIX_CREATE: 'aiMateRemixCreate',
	REMIX_OPEN_MENU: 'aiMateRemixOpenMenu',
	REMIX_SELECT_OPTION: 'aiMateRemixSelectOption',
	REMIX_SHOW_LOADING: 'aiMateRemixShowLoading',
	REMIX_REGENERATE: 'aiMateRemixRegenerate',
	REMIX_INSERT: 'aiMateRemixInsert',
	REMIX_PASTE: 'aiMateRemixPaste',
	REMIX_DIAGRAM_PASTE_FINALIZE: 'aiMateRemixDiagramPasteFinalize',
	FETCH_TASKS: 'aiMateFetchTasks',
	LOAD_STICKY_SKILL_LISTENER: 'aiMateLoadStickySkillListener',
	/**
	 * Tracks duplicating a Remix/MAUI app when its link is pasted into another
	 * Confluence page. Started when the editor plugin detects a MAUI link in
	 * the paste, succeeded once the duplicated app's URL has been swapped into
	 * the editor doc, and failed if the duplicate API call (or any JS error in
	 * the duplication flow) fails. When this experience fails, the
	 * REMIX_INSERT experience is also failed so the overall paste-and-insert
	 * flow is reflected in the SLO.
	 */
	REMIX_DUPLICATE_APP: 'aiMateRemixDuplicateApp',
	STUDIO_CREATE_AGENT: 'aiMateStudioCreateAgent',
	STUDIO_CREATE_AGENT_ASSIGN: 'aiMateStudioCreateAgentAssign',
	STUDIO_CREATE_AGENT_INVOKE: 'aiMateStudioCreateAgentInvoke',
	STUDIO_CREATE_AGENT_BUILD_AND_CHAT: 'aiMateStudioCreateAgentBuildAndChat',
	STUDIO_CREATE_AGENT_REFINE_IN_STUDIO: 'aiMateStudioCreateAgentRefineInStudio',
	STUDIO_CREATE_AGENT_RENDER: 'aiMateStudioCreateAgentRender',
	JIRA_SAVE_DRAFT_WORK_ITEM: 'aiMateJiraSaveDraftWorkItem',
	JIRA_RESOLVE_DRAFT_WORK_ITEM: 'aiMateJiraResolveDraftWorkItem',
	JIRA_CREATE_WORK_ITEMS_BULK: 'aiMateJiraCreateWorkItemsBulk',
	ROVO_SKILLS_PLAN_WORK_ITEM_CREATE: 'RovoSkillsPlanWorkItemCreate',
	ROVO_SKILLS_EXECUTION_WORK_ITEM_CREATE: 'RovoSkillsExecutionWorkItemCreate',
	ROVO_SKILLS_PLAN_WORK_ITEM_UPDATE: 'RovoSkillsPlanWorkItemUpdate',
	ROVO_SKILLS_EXECUTION_WORK_ITEM_UPDATE: 'RovoSkillsExecutionWorkItemUpdate',
	/**
	 * Tracks the sender's end-to-end conversation-sharing funnel as a single
	 * experience. Started when the share modal opens and succeeded only after
	 * an existing or newly created share link is copied to the clipboard.
	 * Unexpected hydration, persistence, share-creation, and clipboard errors
	 * fail the experience. A `404` when loading existing share details is the
	 * expected "not shared yet" state and does not fail the experience. Closing
	 * the modal before any terminal state is recorded aborts the experience so
	 * abandonment is excluded from the SLO.
	 */
	SHARE_CONVERSATION_SESSION: 'aiMateShareConversationSession',
	/**
	 * Tracks the first action that reaches a terminal event while the Shared
	 * chats tab in Rovo settings is active. Preview succeeds with `action` set
	 * to `preview`; deleting a share or conversation succeeds or fails with
	 * `action` set to `delete`. Later or overlapping terminal calls are ignored
	 * by the underlying tracker. Closing the tab first aborts the experience.
	 */
	MANAGE_CONVERSATION_SHARES: 'aiMateManageConversationShares',
	/**
	 * Tracks loading a shared conversation on the shared conversation preview
	 * page, for both the sender previewing their own share and a recipient
	 * opening a share link. Started when the route mounts and succeeded only
	 * after the conversation has rendered. Errors retrieving or mapping the
	 * share fail the experience. A link that never carried the parameters needed
	 * to request a share aborts the experience, as does leaving the page before
	 * the conversation renders, so malformed links and abandonment are still
	 * recorded without counting against the SLO.
	 */
	VIEW_CONVERSATION_SHARE: 'aiMateViewConversationShare',
	/**
	 * Register new values as possible `task` values in the DataPortal attributes
	 * tab or their events will be dropped. Experiences available in the mini
	 * modal also emit with a `.miniModal` suffix, which must be registered
	 * separately. See the guide in
	 * https://hello.atlassian.net/wiki/spaces/AA6/pages/3961393753/HOWTO+Create+FE+Reliability+SLO+using+experience+tracker
	 */
} as const satisfies {
	[key: string]: `aiMate${string}` | `csm${string}` | `RovoSkills${string}`;
};
export type ExperienceNameType = (typeof ExperienceName)[keyof typeof ExperienceName];
export enum ExperienceNamePostfix {
	MINI_MODAL = 'miniModal',
}
type SendMessageExperienceNameParams = {
	deepResearchEnabled?: boolean;
	thinkDeeperEnabled?: boolean;
	taskModeEnabled?: boolean;
	fastModeEnabled?: boolean;
	isCustomRovoAgent?: boolean;
};
export const sendMessageExperienceName = ({
	deepResearchEnabled,
	thinkDeeperEnabled,
	taskModeEnabled,
	fastModeEnabled,
	isCustomRovoAgent,
}: SendMessageExperienceNameParams):
	| 'aiMateSendMessage'
	| 'aiMateSendMessageRovoCustomAgent'
	| 'aiMateSendDRMessage'
	| 'aiMateSendThinkDeeperMessage'
	| 'aiMateSendTaskModeMessage' => {
	if (isCustomRovoAgent) {
		return ExperienceName.SEND_MESSAGE_ROVO_CUSTOM_AGENT;
	}

	if (deepResearchEnabled) {
		return ExperienceName.SEND_DR_MESSAGE;
	}

	if (thinkDeeperEnabled) {
		return ExperienceName.SEND_TD_MESSAGE;
	}

	if (taskModeEnabled) {
		return ExperienceName.SEND_TASK_MODE_MESSAGE;
	}

	if (fastModeEnabled) {
		// Sending the same experience name for fast mode as it is the same as the default (auto) mode
		return ExperienceName.SEND_MESSAGE;
	}

	return ExperienceName.SEND_MESSAGE;
};

export const AIFC_EXPERIENCES_TO_FAIL: (
	| 'aiMateAIFCFinalizeContent'
	| 'aiMateLoadStagingAreaModal'
	| 'aiMateAifcGenerateContent'
	| 'aiMateAifcGenerateContentInChat'
	| 'aiMateAifcUpdateContent'
	| 'aiMateAifcModalSession'
	| 'aiMateRemixRegenerate'
)[] = [
	ExperienceName.AIFC_GENERATE_CONTENT,
	ExperienceName.AIFC_GENERATE_CONTENT_IN_CHAT,
	ExperienceName.AIFC_UPDATE_CONTENT,
	ExperienceName.AIFC_FINALIZE_CONTENT,
	ExperienceName.AIFC_LOAD_STAGING_AREA_MODAL,
	ExperienceName.AIFC_MODAL_SESSION,
	ExperienceName.REMIX_REGENERATE,
];

/**
 * Reasons why an experience failure was excluded from the main SLO and
 * converted to taskAbort. Used as an attribute on the aiMateFilteredClientError
 * experience so failures can be grouped/filtered by category on TOME.
 */
export const SLOExclusionReason = {
	/** Browser-level network failure — request never reached Atlassian servers (e.g. VPN, firewall, no connectivity) */
	CLIENT_FETCH_ERROR: 'clientFetchError',
	/** Rovo is not enabled for the user's organisation (product-not-active stream error) */
	PRODUCT_NOT_ACTIVE: 'productNotActive',
	/** User is not authenticated or lacks permission (HTTP 401/403) */
	AUTH_OR_PERMISSION_ERROR: 'authOrPermissionError',
} as const;

export type SLOExclusionReasonType = (typeof SLOExclusionReason)[keyof typeof SLOExclusionReason];

/**
 * Attribute keys recording *when* the PREVIEW_CARD_RENDER experience
 * (`aiMatePreviewCardRender`) reached each lifecycle stage.
 *
 * Each attribute should store a `performance.now()` timestamp (in ms) set via
 * `updateAttributes` at the moment that stage is reached.
 */
export const PreviewCardRenderStage = {
	/** Experience started (intent detection decided on a creatable/Remix action) */
	STARTED_AT: 'previewCardRenderStartedAtMs',
	/** Most recent ACTION_PART received */
	ACTION_PART_AT: 'previewCardRenderActionPartAtMs',
	/** ACTION_COMPLETE received */
	ACTION_COMPLETE_AT: 'previewCardRenderActionCompleteAtMs',
	/** SmartCreatePreviewCard mounted */
	CARD_MOUNTED_AT: 'previewCardRenderCardMountedAtMs',
	/** SmartCreatePreviewCard mounted but used a non-instrumented branch. */
	ROUTED_TO_NON_INSTRUMENTED_BRANCH_AT: 'previewCardRenderRoutedToNonInstrumentedBranchAtMs',
} as const;

/**
 * Attribute keys recording the streamed action's `status` on the PREVIEW_CARD_RENDER
 * experience (`aiMatePreviewCardRender`).
 *
 * The preview card is only rendered once the action status is no longer `preparing`,
 * so these let a render timeout be correlated with the status the gate filtered on.
 * `ACTION_COMPLETE_KEY` is a sanity check that a single message did not stream
 * action chunks under more than one key.
 *
 * A chunk that carries no status records `'missing'`, so an absent attribute always
 * means that stream stage was never reached.
 */
export const PreviewCardRenderActionAttribute = {
	/** Action status on the ACTION_PREPARE chunk */
	ACTION_PREPARE_STATUS: 'previewCardRenderActionPrepareStatus',
	/** Action status on the most recent ACTION_PART chunk */
	ACTION_PART_STATUS: 'previewCardRenderActionPartStatus',
	/** Action status on the ACTION_COMPLETE chunk */
	ACTION_COMPLETE_STATUS: 'previewCardRenderActionCompleteStatus',
	/** Action key on the ACTION_COMPLETE chunk */
	ACTION_COMPLETE_KEY: 'previewCardRenderActionCompleteKey',
} as const;

// Timestamps for each stage of the PREVIEW_CONTENT experience, set via updateAttributes.
export const PreviewContentStage = {
	STARTED_AT: 'previewContentStartedAtMs',
	EMBED_URL_RESOLVED_AT: 'previewContentEmbedUrlResolvedAtMs',
	IFRAME_MOUNTED_AT: 'previewContentIframeMountedAtMs',
	HANDSHAKE_RECEIVED_AT: 'previewContentHandshakeReceivedAtMs',
	IFRAME_LOADED_AT: 'previewContentIframeLoadedAtMs',
} as const;

// Note, experiences with timeouts must be listed first
export const AIFC_EXPERIENCES_TO_ABORT: (
	| 'aiMateAIFCFinalizeContent'
	| 'aiMatePreviewContent'
	| 'aiMateLoadStagingAreaModal'
	| 'aiMateAifcGenerateContent'
	| 'aiMateAifcGenerateContentInChat'
	| 'aiMateAifcInitialDataReceived'
	| 'aiMateAifcAllDataHandled'
	| 'aiMateAifcUpdateContent'
	| 'aiMateAifcModalSession'
	| 'aiMateRemixRegenerate'
)[] = [
	ExperienceName.AIFC_INITIAL_DATA_RECEIVED,
	ExperienceName.AIFC_ALL_DATA_HANDLED,
	ExperienceName.PREVIEW_CONTENT,
	...AIFC_EXPERIENCES_TO_FAIL,
];

// These UFO marks are used to break the experience down into different timing parts.
// backend (SUBMIT → ACTION_COMPLETE_RECEIVED) and render (ACTION_COMPLETE_RECEIVED → PREVIEW_VISIBLE) so we can see where time is being spent.
export const REMIX_GENERATE_PREVIEW_MARKS: Record<string, string> = {
	SUBMIT: 'submit',
	ACTION_COMPLETE_RECEIVED: 'action_complete_received',
	PREVIEW_VISIBLE: 'preview_visible',
};

export const REMIX_GENERATE_PREVIEW_TIMINGS: ReadonlyArray<{
	key: string;
	startMark: string;
	endMark: string;
}> = [
	{
		key: 'backend_latency',
		startMark: REMIX_GENERATE_PREVIEW_MARKS.SUBMIT,
		endMark: REMIX_GENERATE_PREVIEW_MARKS.ACTION_COMPLETE_RECEIVED,
	},
	{
		key: 'render_latency',
		startMark: REMIX_GENERATE_PREVIEW_MARKS.ACTION_COMPLETE_RECEIVED,
		endMark: REMIX_GENERATE_PREVIEW_MARKS.PREVIEW_VISIBLE,
	},
];

// UFO experience to measure e2e latency from when a user triggers a remix to when the preview is visible.
export const remixGeneratePreviewExperience: UFOExperience = new UFOExperience(
	'remix-generate-preview',
	{
		type: ExperienceTypes.Experience,
		performanceType: ExperiencePerformanceTypes.InlineResult,
		timings: [...REMIX_GENERATE_PREVIEW_TIMINGS],
	},
);

// This breaks down the timings for the "edit existing" remix flow.
export const REMIX_EDIT_EXISTING_MARKS: Record<string, string> = {
	SUBMIT: 'submit', // mark the start of the experience when the user submits the edit request
	ACTION_COMPLETE_RECEIVED: 'action_complete_received', // mark when we receive the ACTION_COMPLETE event from the backend
	NODE_INSERTED: 'node_inserted', // mark when the new app node is inserted into the editor
};

export const REMIX_EDIT_EXISTING_TIMINGS: ReadonlyArray<{
	key: string;
	startMark: string;
	endMark: string;
}> = [
	{
		key: 'backend_latency',
		startMark: REMIX_EDIT_EXISTING_MARKS.SUBMIT,
		endMark: REMIX_EDIT_EXISTING_MARKS.ACTION_COMPLETE_RECEIVED,
	},
	{
		key: 'insertion_latency',
		startMark: REMIX_EDIT_EXISTING_MARKS.ACTION_COMPLETE_RECEIVED,
		endMark: REMIX_EDIT_EXISTING_MARKS.NODE_INSERTED,
	},
];

// UFO experience to measure backend latency from when a user submits an edit
// to an existing remix/MAUI app embed in the inline AI Dialog, until the new
// app node is inserted into the editor.
export const remixEditExistingExperience: UFOExperience = new UFOExperience('remix-edit-existing', {
	type: ExperienceTypes.Experience,
	performanceType: ExperiencePerformanceTypes.InlineResult,
	timings: [...REMIX_EDIT_EXISTING_TIMINGS],
});

export const REMIX_PASTE_RENDER_EMBED_MARKS: Record<string, string> = {
	PASTE_DETECTED: 'paste_detected',
	EMBED_LOADED: 'embed_loaded',
};

export const REMIX_PASTE_RENDER_EMBED_TIMINGS: ReadonlyArray<{
	key: string;
	startMark: string;
	endMark: string;
}> = [
	{
		key: 'render_latency',
		startMark: REMIX_PASTE_RENDER_EMBED_MARKS.PASTE_DETECTED,
		endMark: REMIX_PASTE_RENDER_EMBED_MARKS.EMBED_LOADED,
	},
];

export const remixPasteRenderEmbedExperience: UFOExperience = new UFOExperience(
	'remix-paste-render-embed',
	{
		type: ExperienceTypes.Experience,
		performanceType: ExperiencePerformanceTypes.InlineResult,
		timings: [...REMIX_PASTE_RENDER_EMBED_TIMINGS],
	},
);

let isRemixPasteRenderEmbedExperienceActive = false;

export const startRemixPasteRenderEmbedExperience = (metadata?: CustomData): void => {
	if (isRemixPasteRenderEmbedExperienceActive) {
		return;
	}
	isRemixPasteRenderEmbedExperienceActive = true;
	remixPasteRenderEmbedExperience.start();
	remixPasteRenderEmbedExperience.mark(REMIX_PASTE_RENDER_EMBED_MARKS.PASTE_DETECTED);
	if (metadata) {
		remixPasteRenderEmbedExperience.addMetadata(metadata);
	}
};

export const succeedRemixPasteRenderEmbedExperience = (metadata?: CustomData): void => {
	if (!isRemixPasteRenderEmbedExperienceActive) {
		return;
	}
	remixPasteRenderEmbedExperience.mark(REMIX_PASTE_RENDER_EMBED_MARKS.EMBED_LOADED);
	if (metadata) {
		remixPasteRenderEmbedExperience.addMetadata(metadata);
	}
	remixPasteRenderEmbedExperience.success();
	isRemixPasteRenderEmbedExperienceActive = false;
};

export const failRemixPasteRenderEmbedExperience = (error: Error, metadata?: CustomData): void => {
	if (!isRemixPasteRenderEmbedExperienceActive) {
		return;
	}
	if (metadata) {
		remixPasteRenderEmbedExperience.addMetadata(metadata);
	}
	remixPasteRenderEmbedExperience.failure({ metadata: { reason: error.message } });
	isRemixPasteRenderEmbedExperienceActive = false;
};
